<template>
  <div class="staff-employees-overview">
    <div class="toolbar">
      <span>Search by first name or last name</span>
      <div class="filters">
        <form class="search" @submit.prevent="onSearch">
          <input
            class="input"
            type="text"
            placeholder="Search by first name or last name"
            v-model="searchFilter"
          />
          <button
            class="update-button"
            type="submit"
            :disabled="employees.refreshing || !searchFilter"
          >
            Search
          </button>
        </form>
        <button class="reset-button" @click="onSearchReset" :disabled="employees.refreshing">
          Reset
        </button>
        <select
          v-if="user.isStaff && !organizations.refreshing"
          class="organizations"
          @change="onOrganizationSelection"
        >
          <option value="" disabled :selected="!selectedOrganization">
            Filter By Organization
          </option>
          <template v-for="org in organizations.list">
            <option :key="org.id" :value="org.id">
              {{ org.name }}
            </option>
            <option
              v-show="false"
              v-for="subOrg in org.subOrganizations"
              :key="subOrg.id"
              :value="subOrg.id"
            >
              {{ subOrg.name }}
            </option>
          </template>
        </select>
        <div v-else-if="user.isStaff && organizations.refreshing" class="organizations placeholder">
          <LoadSpinner />
        </div>
        <button
          v-if="user.isStaff"
          class="reset-button"
          @click="onOrganizationReset"
          :disabled="organizations.refreshing || !selectedOrganization"
        >
          Reset
        </button>
      </div>
    </div>
    <div class="staff-employees-overview__inner">
      <div v-if="showUpwardFundFilters" class="staff-employees-overview__filters">
        <h2>Filters</h2>
        <div class="nominator-filter">
          <md-field>
            <label>Select a Nominator</label>
            <md-select
              @md-selected="filterBySelectedNominator"
              v-model="selectedNominator"
            >
              <md-option value="">All</md-option>
              <md-option v-for="nominator in getNominatorsList" :key="nominator.id"
              :value="nominator.id">
                {{ nominator.firstName }} {{ nominator.lastName }}
              </md-option>
            </md-select>
          </md-field>
        </div>
      </div>
      <div class="staff-employees-overview__table">
        <TableHeader :title="tableName" :collection="employees" />
        <LoadSpinner v-if="employees.refreshing" style="padding: 2rem 0" />
        <div class="table-wrapper" v-if="!employees.refreshing">
          <md-table>
            <md-table-row class="header-row">
              <md-table-head>
                Name
                <span class="sort-icon" @click="onSortByField('last_name')">
                  <md-icon v-if="sorts.last_name === null">sort</md-icon>
                  <md-icon v-else-if="sorts.last_name === ASC">arrow_drop_down</md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <md-table-head>
                Organization
                <span class="sort-icon" @click="onSortByField('organization__name')">
                  <md-icon v-if="sorts.organization__name === null">sort</md-icon>
                  <md-icon v-else-if="sorts.organization__name === ASC">arrow_drop_down</md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <md-table-head v-if="organizationDetails.subOrganizations.length">
                Sub Organization
                <span class="sort-icon" @click="onSortByField('sub_organization__name')">
                  <md-icon v-if="sorts.sub_organization__name === null">sort</md-icon>
                  <md-icon v-else-if="sorts.sub_organization__name === ASC">arrow_drop_down</md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <md-table-head>
                Unique ID
                <span class="sort-icon" @click="onSortByField('employee_code__code')">
                  <md-icon v-if="sorts.employee_code__code === null">sort</md-icon>
                  <md-icon v-else-if="sorts.employee_code__code === ASC">arrow_drop_down</md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <md-table-head>
                Date Joined
                <span class="sort-icon" @click="onSortByField('datetime_created')">
                  <md-icon v-if="sorts.datetime_created === null">sort</md-icon>
                  <md-icon v-else-if="sorts.datetime_created === ASC">arrow_drop_down</md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <md-table-head>
                Is Active
                <span class="sort-icon" @click="onSortByField('is_active')">
                  <md-icon v-if="sorts.is_active === null">sort</md-icon>
                  <md-icon v-else-if="sorts.is_active === ASC">arrow_drop_down</md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
            </md-table-row>

            <md-table-row
              class="body-row"
              v-for="employee in employees.list"
              :key="employee.id"
              @click.native="() => onRowClick(employee)"
            >
              <md-table-cell>{{ employee.fullName || 'Unnamed User' }}</md-table-cell>
              <md-table-cell>{{
                (employee.organizationRef && employee.organizationRef.name) || 'Unnamed Org.'
              }}</md-table-cell>
              <md-table-cell v-if="organizationDetails.subOrganizations.length">{{
                (employee.subOrganizationRef && employee.subOrganizationRef.name) || ''
              }}</md-table-cell>
              <md-table-cell>{{
                (employee.employeeCodeRef && employee.employeeCodeRef.code) || 'N/A'
              }}</md-table-cell>
              <md-table-cell>{{
                employee.datetimeCreated &&
                  employee.datetimeCreated | formatDateShort($language.current)
              }}</md-table-cell>
              <md-table-cell>{{ employee.isActive ? 'Yes' : 'No' }}</md-table-cell>
            </md-table-row>
          </md-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { LoadSpinner, TableHeader } from '@/components'

import Employee from '@/services/employees'
import Organization from '@/services/organizations'
import SelectOrganization from '@/services/selectOrganizations'
import CollectionManager from '@/services/collectionManager'
import Pagination from '@/services/pagination'
import AdminPermissions from '@/services/adminPermissions'
import Report from '@/services/reporting'

const ASC = 'ASC'
const DESC = 'DESC'

export default {
  name: 'EmployeesOverview',
  components: {
    LoadSpinner,
    TableHeader,
  },
  data: function() {
    return {
      employees: CollectionManager.create({
        ModelClass: Employee,
      }),
      organizations: CollectionManager.create({
        ModelClass: SelectOrganization,
        pagination: Pagination.create({ pageSize: 100 }),
      }),
      searchFilter: '',
      selectedOrganization: null,
      selectedSubOrganization: null,
      tableName: '',
      selectedOrgObj: null,
      sorts: {
        last_name: null,
        organization__name: null,
        sub_organization__name: null,
        employee_code__code: null,
        datetime_created: null,
        is_active: null,
      },
      ASC,
      DESC,
      nominationsReport: null,
      selectedNominator: null,
    }
  },
  created() {
    this.refresh()
    if (this.user.isStaff) {
      this.organizations.refresh()
    }
  },
  methods: {
    refresh() {
      this.employees.refresh()

      // Sets the table name based on the selected organization
      this.selectedOrgObj = this.organizations.list.find(
        obj => obj.id === this.selectedOrganization,
      )

      // Upward Fund get nominations report for nominator filtering
      if (this.selectedOrgObj && this.selectedOrgObj.isTcrOrganization) {
        this.getNominationsReport()
      } else {
        this.nominationsReport = null
        this.selectedNominator = null
      }

      if (this.user.isStaff) {
        this.tableName = 'User'
        return
      }

      if (
        this.selectedOrgObj &&
        this.selectedOrgObj.customVariables &&
        this.selectedOrgObj.customVariables.cv3
      ) {
        this.tableName = this.selectedOrgObj.customVariables.cv3 + 's'
        return
      }

      if (
        this.organizationDetails.customVariables &&
        this.organizationDetails.customVariables &&
        this.organizationDetails.customVariables.cv3
      ) {
        this.tableName = this.organizationDetails.customVariables.cv3 + 's'
        return
      }

      this.tableName = 'Users'
    },
    onRowClick(employee) {
      const routeData = this.$router.resolve({
        name: 'StaffEmployeeDetail',
        params: {
          id: employee.id,
        },
      })

      window.open(routeData.href, '_blank')
    },
    updateEmployees(filtersObj = {}) {
      this.employees = CollectionManager.create({
        ModelClass: Employee,
        filters: {
          ...this.employees.filters,
          ...filtersObj,
        },
      })
      this.refresh()
    },
    onSearch() {
      this.updateEmployees({ search: this.searchFilter })
    },
    onSearchReset() {
      this.searchFilter = ''
      this.updateEmployees({ search: '' })
    },
    onOrganizationSelection({ target: { value } }) {
      if (this.organizations.list.filter(org => org.id == value).length) {
        this.selectedOrganization = value
        this.updateEmployees({ organization: this.selectedOrganization, subOrganization: null })

      } else {
        this.selectedSubOrganization = value
        this.updateEmployees({ organization: null, subOrganization: this.selectedSubOrganization })
      }
    },
    onOrganizationReset() {
      this.selectedOrganization = null
      this.updateEmployees({ organization: null, subOrganization: null })
    },
    onSortByField(field) {
      // toggle ascending / descending, while resetting other sorts (currently only filtering by one at a time)
      let ascending = this.sorts[field] === DESC || this.sorts[field] == null
      let newSortState = {
        last_name: null,
        organization__name: null,
        sub_organization__name: null,
        employee_code__code: null,
        datetime_created: null,
        is_active: null,
      }
      newSortState[field] = ascending ? ASC : DESC
      this.sorts = newSortState
      // update employees
      let term = `${ascending ? '' : '-'}${field}`
      // if sorting by name, include first_name as second sorting criteria
      if (field == 'last_name') {
        term += `,${ascending ? '' : '-'}${'first_name'}`
      }
      this.updateEmployees({ ordering: term })
    },
    getNominationsReport() {
      this.loading = true
      Report.api
        .retrieveNominations(this.selectedOrgObj.id, null, null, null)
        .then((nominationsReport) => {
          this.nominationsReport = nominationsReport
        })
        .finally(() => {
          this.loading = false
        })
    },
    filterBySelectedNominator() {
      if(!this.selectedNominator) {
        this.employees.filters.nominator = null
        this.employees.refresh()
      }

      this.updateEmployees({ nominator: this.selectedNominator })
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    isStaffMember() {
      return this.$store.state.user.isStaff
    },
    showUpwardFundFilters() {
      // No organization selected
      if(!this.selectedOrganization || !this.selectedOrgObj)
        return false

      // Determine if our selected organization is TCR organization
      let isTcrOrganization = this.selectedOrgObj.isTcrOrganization
      if(!isTcrOrganization && this.isStaffMember)
        return false

      // If the user is staff or a TCR admin show the tab
      if(isTcrOrganization && (this.isStaffMember || (this.user.isAdmin && this.user.adminPermissions.name === AdminPermissions.permissionNames.TCR_ADMIN)))
        return true

      return false
    },
    getNominatorsList() {
      if(!this.nominationsReport)
        return []

      return this.nominationsReport['nominators']
    }
  },
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables';
@import '../../styles/mixins/containers';
@import '../../styles/input';
@import '../../styles/mixins/buttons';

.staff-employees-overview {
  @include screen-container($mobile-padding: 1rem 0.5rem);

  &__inner {
    max-width: 100%;
  }

  @media (min-width: $mobile-break-point) {
    &__inner {
      display: flex;
    }

    &__filters {
      flex: 1;
      padding: 1rem 2rem 0 1rem;
      min-width: 16rem;
    }

    &__table {
      flex: 8;
      overflow-x: auto;
    }
  }
}

// ToolBar Styles
.toolbar {
  margin-bottom: 2rem;
}

.filters {
  display: flex;
  flex-flow: row;
  align-items: center;

  .search {
    display: flex;
    flex-flow: row;
    align-items: center;
    width: 40rem;
  }

  .input {
    @include base-input;
    text-align: left;
    margin-right: 2rem;
  }

  .update-button {
    @include primary-button();
    min-width: unset;
    width: 6rem;
    height: 2rem;
    display: flex;
    flex-flow: row;
    justify-content: center;
    align-items: center;
  }

  .reset-button {
    @include primary-button-selected();
    min-width: unset;
    width: 6rem;
    height: 2rem;
    display: flex;
    flex-flow: row;
    justify-content: center;
    margin-left: 2rem;
    align-items: center;
  }

  .organizations {
    width: 19rem;
    height: 2.25rem;
    font-family: $base-font-family;
    font-size: 1rem;
    margin-left: auto;
    padding: 0.2rem;
  }

  .placeholder {
    width: 19rem;
    height: 2.25rem;
    font-family: $base-font-family;
    font-size: 1rem;
    border-radius: 5px;
    border: 1px solid #a6a6a6;
    background-color: #f8f8f8;
    display: flex;
    flex-flow: row;
    align-items: center;
    justify-content: center;
    margin-left: auto;
  }
}

// Table Styles
.table-header {
  display: flex;
  margin-bottom: 1rem;

  &__start {
    display: flex;
    padding: 1rem 0 0 0;
  }

  &__end {
    flex: 4;
    align-self: flex-end;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}

.sort-icon {
  &:hover {
    cursor: pointer;
  }
}

.table-wrapper {
  max-width: 100%;
  overflow-x: auto;
}

.header-row {
  background-color: $soft-gray;

  & > .md-table-head {
    font-size: 1rem;
  }
}

.body-row {
  & > .md-table-cell {
    border-top: 1px solid $soft-gray;
    padding: 0.5rem;
  }

  &:hover {
    background-color: $off-white;
    cursor: pointer;
  }
}
</style>
